import React, { useEffect, useState } from "react";
import style from "./BookedDebitNote.module.css";
import axios from "axios";
import moment from "moment";
import DateRangePicker from "./DateRangePicker";
import { toast } from "react-toastify";
import pdfIcon from "./assets/pdf.png";
import { API_BASE_URL } from "./config.js";

/* ---------- Helpers ---------- */
const formatDateForAPI = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}${String(d.getDate()).padStart(2, "0")}`;
};

const BookedDebitNote = () => {
  const vendorDataRaw = sessionStorage.getItem("vendordata");
  const vendorInfo = vendorDataRaw ? JSON.parse(vendorDataRaw) : null;
  const vendorCode = vendorInfo?.CardCode || "";

  const [selectedLocation, setSelectedLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [selectedValue, setSelectedValue] = useState("C");

  const [bookeddebitnoteData, setBookedDebitNoteData] = useState([]);

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [fromDate, setFromDate] = useState(
    firstDayOfMonth.toISOString().split("T")[0]
  );
  const [toDate, setToDate] = useState(
    lastDayOfMonth.toISOString().split("T")[0]
  );

  /* 🔴 PDF downloading state (row-level) */
  const [downloadingDocEntry, setDownloadingDocEntry] = useState(null);

  /* ---------- PDF Download ---------- */
  const handleDownloadPDF = async (item) => {
    try {
      setDownloadingDocEntry(item.DocEntry);

      const response = await fetch(
        `${API_BASE_URL}/BookedDebitNotes/DownloadSAPPDF?DocNum=${item.DocEntry}`
      );

      if (!response.ok) throw new Error("Failed to download PDF");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `DebitNote_${item.StarDocNum}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error("Could not download PDF.");
    } finally {
      setDownloadingDocEntry(null);
    }
  };

  /* ---------- Data Fetch ---------- */
  const fetchBookedDebitNoteData = async (from, to, status, location) => {
    if (!vendorCode) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/BookedDebitNotes?VendorCode=${vendorCode}&FromDt=${formatDateForAPI(
          from
        )}&ToDt=${formatDateForAPI(to)}&Status=${status}&Location=${location}`
      );
      setBookedDebitNoteData(res.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch data.");
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/location`);
      setLocations(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Failed to fetch locations.");
    }
  };

  /* ---------- Events ---------- */
  const handleDateChange = (start, end) => {
    setFromDate(start);
    setToDate(end);
    fetchBookedDebitNoteData(start, end, selectedValue, selectedLocation);
  };

  const handleSearch = () => {
    fetchBookedDebitNoteData(fromDate, toDate, selectedValue, selectedLocation);
  };

  /* ---------- Initial Load ---------- */
  useEffect(() => {
    fetchLocations();
    const start = moment().startOf("quarter").format("YYYY-MM-DD");
    const end = moment().endOf("quarter").format("YYYY-MM-DD");
    setFromDate(start);
    setToDate(end);
    fetchBookedDebitNoteData(start, end, selectedValue, selectedLocation);
  }, [vendorCode]);

  /* ---------- UI ---------- */
  return (
    <div className={style.BookedInvoiceDetails}>
      <div className={style.BookedInvoiceDetailsContainer}>
        <div className={style.MainBookedInvoiceDetailsContainer}>
          <div className={style.filterArea}>
            <div className={style.pofilter}>
              <div>Status</div>
              <select
                value={selectedValue}
                onChange={(e) => {
                  setSelectedValue(e.target.value);
                  fetchBookedDebitNoteData(
                    fromDate,
                    toDate,
                    e.target.value,
                    selectedLocation
                  );
                }}
              >
                <option value="O">Open</option>
                <option value="C">Closed</option>
              </select>

              <div>Location</div>
              <select
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  fetchBookedDebitNoteData(
                    fromDate,
                    toDate,
                    selectedValue,
                    e.target.value
                  );
                }}
              >
                <option value="">All</option>
                {locations.map((loc) => (
                  <option key={loc.Code} value={loc.Code}>
                    {loc.Location}
                  </option>
                ))}
              </select>
            </div>

            <div className={style.datearea}>
              <DateRangePicker
                onDateChange={handleDateChange}
                startDate={fromDate}
                endDate={toDate}
              />
              <button onClick={handleSearch}>Search</button>
            </div>
          </div>

          <div className={style.MainArea}>
            <div className={style.BookedInvoicetable}>
              <table>
                <thead>
                  <tr>
                    <td>Customer Booking #</td>
                    <td>Debit Note Date</td>
                    <td>Debit Note Ref #</td>
                    <td>Debit Note Ref Date</td>
                    <td>Taxable Amount</td>
                    <td>Tax Amount</td>
                    <td>Total Amount</td>
                    <td>Location</td>
                    <td>Action</td>
                  </tr>
                </thead>

                <tbody>
                  {bookeddebitnoteData.length ? (
                    bookeddebitnoteData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.StarDocNum}</td>
                        <td>{item.StarDocDate}</td>
                        <td>{item.VendorDocNo}</td>
                        <td>{item.VendorDocDate || "-"}</td>
                        <td className="text-end amount-cell">
                          {item.DocCur}{" "}
                          {Number(item.TaxableAmt).toLocaleString("en-IN")}
                        </td>
                        <td className="text-end amount-cell">
                          {item.DocCur}{" "}
                          {Number(item.TaxAmt).toLocaleString("en-IN")}
                        </td>
                        <td className="text-end amount-cell">
                          {item.DocCur}{" "}
                          {Number(item.DocTotal).toLocaleString("en-IN")}
                        </td>
                        <td>{item.Location || "-"}</td>

                        <td
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 10,
                          }}
                        >
                          {downloadingDocEntry === item.DocEntry ? (
                            <span style={{ fontSize: 12, color: "#666" }}>
                              Downloading...
                            </span>
                          ) : (
                            <img
                              src={pdfIcon}
                              alt="PDF"
                              title="Download PDF"
                              style={{
                                width: 20,
                                height: 20,
                                cursor: "pointer",
                              }}
                              onClick={() => handleDownloadPDF(item)}
                            />
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" style={{ textAlign: "center" }}>
                        No Booked Debit Note Found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookedDebitNote;
